import { useMemo, useState } from 'react';
import { useAppInsightsContext, useTrackEvent } from '@microsoft/applicationinsights-react-js';

import {
  PayrollReportHeadViewModel,
  PayrollReportStatusEnum,
  PayrollReportSummaryViewModel,
  ReportingReceiptViewModel,
} from '@fora-monorepo/employer-my-pages-common/types';
import { Assets, Button, Flex, Text } from '@foradevelopment/fora-ui';
import { getReadableTimestamp } from '@fora-monorepo/date-utils';
import { FORA_SOURCE } from '@fora-monorepo/fora-common';

import { ReportModal } from '../Report/ReportModal';
import { HistoryLogItem, HistoryLogList } from './HistoryLogList';
import { useTypedTranslation } from '../../i18n/i18n';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getPayrollReportSummary } from '../../redux/report/reportThunks';
import { DownloadButtonDeviation } from '../DownloadButtons/DownloadButtonDeviation';
import { DownloadButtonInformation } from '../DownloadButtons/DownloadButtonInformation';
import { DownloadButtonReportDetails } from '../DownloadButtons/DownloadButtonReportDetails';

export const HistoryLogReports = ({ reports }: { reports: PayrollReportHeadViewModel[] }) => {
  const { t } = useTypedTranslation();

  return (
    <HistoryLogList title={t('tbd.historyLogHeading')}>
      {reports.map((report, index) => (
        <LogItem {...report} key={index} index={index} />
      ))}
    </HistoryLogList>
  );
};

const LogItem = ({
  adminReporterFullName,
  createdDate,
  hasEmploymentDeviations,
  hasEmploymentInformation,
  payMonths,
  payrollReportId: reportId,
  source,
  status,
  index,
}: PayrollReportHeadViewModel & { index: number }) => {
  const dispatch = useAppDispatch();
  const { t } = useTypedTranslation();
  const {
    partnerNumber: partnerNumberOrganization,
    organizationName: employerName,
    organizationNumber,
  } = useAppSelector(state => state.employer.employerInfo);

  const getPayrollReportSummaryById = async () => {
    const result = await dispatch(getPayrollReportSummary({ partnerNumberOrganization, reportId }));
    if (getPayrollReportSummary.fulfilled.match(result) && result.payload) {
      return result.payload;
    }
    return Promise.reject('Could not get report details');
  };

  const getReportReceiptModalData = async (): Promise<ReportingReceiptViewModel> => {
    const reportSummary: PayrollReportSummaryViewModel = await getPayrollReportSummaryById();
    return {
      organizationName: reportSummary.organizationName,
      organizationNumber: reportSummary.organizationNumber,
      employmentCount: reportSummary.employmentCount,
      payMonthSummaries: reportSummary.payMonthSummary,
      reportDate: getReadableTimestamp(createdDate),
      reporterName: adminReporterFullName || '',
      source: source,
    };
  };

  const downloadButtonProps = useMemo(
    () => ({ reportId, partnerNumberOrganization, organizationNumber, employerName, createdDate }),
    [createdDate, employerName, organizationNumber, partnerNumberOrganization, reportId]
  );

  return (
    <HistoryLogItem date={createdDate} index={index}>
      <Text size="small" color="black70">
        {t('list.historyLog.adminReportedPayrollText', {
          adminReportedFullName: adminReporterFullName,
          reportSourcePayroll:
            source === FORA_SOURCE
              ? t('section.reportDetails.reportMethodManualInline')
              : t('section.reportDetails.reportMethodFileInline'),
        })}
      </Text>
      {status === PayrollReportStatusEnum.PROCESSING ? (
        <Text size="small" color="black70">
          {t('status.reportProcessing.paymonthOverviewExpanded')}
        </Text>
      ) : (
        <Flex flexDirection="column" alignItems="flex-start" rowGap={1}>
          <ReportModalAndButton getReportReceiptModalData={getReportReceiptModalData} />
          <DownloadButtonReportDetails {...downloadButtonProps} fontSize="small" />
          {(payMonths.some(payMonth => payMonth.hasInformation) || hasEmploymentInformation) && (
            <DownloadButtonInformation {...downloadButtonProps} fontSize="small" />
          )}
          {(payMonths.some(payMonth => payMonth.hasDeviations) || hasEmploymentDeviations) && (
            <DownloadButtonDeviation {...downloadButtonProps} fontSize="small" />
          )}
        </Flex>
      )}
    </HistoryLogItem>
  );
};

export const ReportModalAndButton = ({
  getReportReceiptModalData,
}: {
  getReportReceiptModalData: () => Promise<ReportingReceiptViewModel>;
}) => {
  const { t } = useTypedTranslation();
  const appInsights = useAppInsightsContext();
  const name = t('list.historyLog.reciept');

  const trackOpenModal = useTrackEvent(appInsights, 'Opened modal', { name }, true);
  const [modalOpen, setModalOpen] = useState(false);

  return (
    <>
      <Button
        styleAs="link"
        fontSize="small"
        onClick={() => {
          setModalOpen(true);
          trackOpenModal({ name });
        }}
      >
        <Assets.FileIcon width={10} marginRight={5} containerSize={12} />
        {name}
      </Button>
      <ReportModal
        isOpen={modalOpen}
        closeModal={() => setModalOpen(false)}
        getReportData={getReportReceiptModalData}
      />
    </>
  );
};
